// src/components/landing/CTA.jsx
import React, { useEffect, useId, useState } from 'react';
import { API_BASE } from '../../utils/apiBase';
import { useReveal } from '../../utils/reveal';

// Mirrors backend ROLE_RULES. Used only until /api/account/schema answers, or if
// it never does — so the selector is never empty.
const ROLE_FALLBACK = [
  ['architect', 'Architect'], ['builder', 'Builder / Developer'],
  ['structural', 'Structural Designer'], ['civil', 'Civil Engineer'],
  ['contractor', 'Contractor'], ['interior', 'Interior Designer'],
  ['vendor', 'Vendor / Supplier'], ['client', 'Client'],
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * "Request access". Controlled inputs and an onClick handler, no <form>.
 *
 * Success is shown only when the server says the request was stored. A thank-you
 * over a request that went nowhere would be the page lying to the one visitor
 * who acted on it.
 */
export default function CTA({ presetRole, onSignIn }) {
  const ref = useReveal();
  const [roles, setRoles] = useState(ROLE_FALLBACK);
  const [role, setRole] = useState(presetRole || 'architect');
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ kind: 'idle', msg: '' });   // idle | sending | done | error
  const roleId = useId();
  const emailId = useId();
  const statusId = useId();

  useEffect(() => { if (presetRole) setRole(presetRole); }, [presetRole]);

  useEffect(() => {
    let dead = false;
    fetch(`${API_BASE}/api/account/schema`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!dead && Array.isArray(d?.roles) && d.roles.length) {
          setRoles(d.roles.map((r) => [r.key, r.label]));
        }
      })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  const submit = async () => {
    const e = email.trim();
    if (!EMAIL_RE.test(e)) {
      setState({ kind: 'error', msg: 'Enter an email address we can reach you at.' });
      return;
    }
    setState({ kind: 'sending', msg: '' });
    try {
      const res = await fetch(`${API_BASE}/api/access-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: e, role }),
      });
      // Read as text first: an empty body from a stopped backend must produce a
      // sentence, not a JSON parse exception.
      const raw = await res.text();
      let d = null;
      try { d = JSON.parse(raw); } catch { d = null; }
      if (res.ok && d?.ok) { setState({ kind: 'done', msg: e }); return; }
      setState({
        kind: 'error',
        msg: d?.error
          || (raw.trim()
            ? `The request was not accepted (HTTP ${res.status}).`
            : 'No reply from the VAUOM server. Please try again shortly.'),
      });
    } catch {
      setState({ kind: 'error', msg: 'Could not reach the VAUOM server. Please try again shortly.' });
    }
  };

  return (
    <section id="access" className="lp-section lp-cta" aria-labelledby="lp-cta-title">
      <div ref={ref} className="lp-wrap lp-cta-inner lp-reveal">
        <h2 id="lp-cta-title" className="lp-h2">See it on your own project.</h2>
        <p className="lp-lead">Tell us your role and we&rsquo;ll get you in.</p>

        {state.kind === 'done' ? (
          <p className="lp-status lp-status--ok lp-cta-done" role="status">
            Thanks — we&rsquo;ve got your request, and we&rsquo;ll be in touch at <strong>{state.msg}</strong>.
          </p>
        ) : (
          <div className="lp-cta-form">
            <div className="lp-field">
              <label htmlFor={roleId} className="lp-label">I am a</label>
              <select id={roleId} className="lp-input" value={role} onChange={(e) => setRole(e.target.value)}>
                {roles.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div className="lp-field lp-field--grow">
              <label htmlFor={emailId} className="lp-label">Work email</label>
              <input
                id={emailId} className="lp-input" type="email" inputMode="email" autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (state.kind === 'error') setState({ kind: 'idle', msg: '' }); }}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                aria-invalid={state.kind === 'error' || undefined}
                aria-describedby={statusId}
              />
            </div>
            <button
              type="button" className="btn primary lp-btn-primary lp-btn-lg lp-cta-btn"
              onClick={submit} disabled={state.kind === 'sending'}
            >
              {state.kind === 'sending' ? 'Sending…' : 'Request access'}
            </button>
          </div>
        )}

        <p id={statusId} className="lp-status lp-status--err" aria-live="polite">
          {state.kind === 'error' ? state.msg : ''}
        </p>
        <p className="lp-muted lp-cta-signin">
          Already have an account?{' '}
          <button type="button" className="lp-link" onClick={onSignIn}>Login</button>
        </p>
      </div>
    </section>
  );
}
